import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Warning } from '@phosphor-icons/react';

/**
 * Modal reutilizable de confirmación de eliminación.
 * Requiere que el usuario escriba "ELIMINAR" y capture un motivo (mín 5 caracteres).
 *
 * Props:
 *   open (bool)              — abierto/cerrado
 *   onOpenChange (fn)        — callback controlado
 *   title (string)           — encabezado
 *   description (string)     — texto explicando el impacto de la acción
 *   itemLabel (string)       — nombre del registro a eliminar (aparece en negrita)
 *   onConfirm (fn(motivo))   — callback que ejecuta la eliminación
 *   loading (bool)           — deshabilita el botón mientras se procesa
 *   confirmWord (string)     — palabra a escribir; por defecto "ELIMINAR"
 */
export const ConfirmDeleteDialog = ({
  open,
  onOpenChange,
  title = 'Confirmar eliminación',
  description = 'Esta acción es irreversible. El registro dejará de aparecer en el sistema.',
  itemLabel,
  onConfirm,
  loading = false,
  confirmWord = 'ELIMINAR',
}) => {
  const [motivo, setMotivo] = useState('');
  const [word, setWord] = useState('');

  useEffect(() => {
    if (!open) {
      setMotivo('');
      setWord('');
    }
  }, [open]);

  const canConfirm = word.trim().toUpperCase() === confirmWord && motivo.trim().length >= 5 && !loading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(motivo.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="confirm-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Warning size={20} weight="fill" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
            {itemLabel && (
              <p>
                Registro: <strong className="break-words">{itemLabel}</strong>
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="delete-motivo">Motivo de la eliminación *</Label>
            <Textarea
              id="delete-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Registro duplicado, error de captura, alumno inexistente…"
              rows={3}
              className="mt-1"
              data-testid="delete-motivo-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Mínimo 5 caracteres. Quedará registrado en auditoría.</p>
          </div>

          <div>
            <Label htmlFor="delete-confirm-word">
              Para confirmar, escribe <strong>{confirmWord}</strong>
            </Label>
            <Input
              id="delete-confirm-word"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              placeholder={confirmWord}
              className="mt-1 font-mono"
              autoComplete="off"
              data-testid="delete-confirm-word-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="delete-cancel">
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="delete-confirm"
          >
            {loading ? 'Eliminando…' : 'Eliminar definitivamente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
